"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import {
  LineFilterErrors,
  LineFilterResults,
} from "@/components/tools/qualitative-text/line-filter-results";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type Combine,
  DEFAULT_FILTER_OPTIONS,
  type FilterAction,
  type FilterOptions,
  filterLines,
  type MatchKind,
  type MatchPosition,
} from "@/lib/tool-kit/qualitative-text/line-filter";

/** English strings. A translated content file overrides these through `ui`. */
export const LINE_FILTER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your text and any file you open are never uploaded.",
  inputLabel: "Text, one item per line",
  inputPlaceholder: "Paste the lines you want to filter.",
  fileLabel: "Or open a text file",
  sample: "Load a sample",
  clear: "Clear",
  termsLabel: "Words, phrases or patterns — one per line",
  termsHintText:
    "Spaces at the start or end of a term are ignored. A term can contain commas.",
  termsHintRegex:
    "Each line is a JavaScript regular expression, tested against one line of text at a time. Flags are set below.",
  termsPlaceholder: "support",
  actionLabel: "What to do with matching lines",
  actions: { keep: "Keep them", remove: "Remove them" } as Record<
    FilterAction,
    string
  >,
  kindLabel: "Terms are",
  kinds: { text: "Plain text", regex: "Regular expressions" } as Record<
    MatchKind,
    string
  >,
  positionLabel: "A line matches when it",
  positions: {
    contains: "contains the term",
    starts: "starts with the term",
    ends: "ends with the term",
    exact: "is exactly the term",
  } as Record<MatchPosition, string>,
  combineLabel: "With several terms, a line matches when it has",
  combines: { any: "any one of them", all: "all of them" } as Record<
    Combine,
    string
  >,
  caseSensitive: "Match case",
  wholeWord: "Whole words only (support does not match unsupported)",
  trim: "Ignore spaces at the ends of each line when matching",
  keepBlank: "Always keep empty lines",
  emptyPrompt: "Paste some text and enter at least one term to filter it.",
  invalidTerm:
    "Term {term} (“{source}”) is not a valid regular expression: {message}. The text has not been filtered.",
  slowTerm:
    "Term {term} (“{source}”) has a repeated group inside a repeated group, which can make the browser freeze on some lines. Rewrite it without the nesting, for example a+ instead of (a+)+.",
  results: "Filtered text",
  noLines: "There are no lines to filter.",
  noMatches: "No line matches these terms.",
  statLines: "Lines in",
  statMatched: "Lines matched",
  statMatches: "Matches in all",
  statKept: "Lines kept",
  statRemoved: "Lines removed",
  outputLabel: "Result",
  copy: "Copy",
  copied: "Copied!",
  download: "Download .txt",
  filename: "filtered-lines.txt",
  termsHeading: "Matches per term",
  colTerm: "Term",
  colLines: "Lines",
  colMatches: "Matches",
  colLine: "Line",
  colText: "Text",
  matchedHeading:
    "one: The matching line|other: First {shown} of {total} matching lines",
  sampleText:
    "Participant 1: I felt supported by my team.\n\nInterviewer: Tell me more about that.\nParticipant 2: The support came late, but support matters.\nNOTE: unsupported claim, check the recording\nParticipant 1: Thanks, that is all.",
  sampleTerms: "support",
} as const;

export default function LineFilterWidget() {
  const strings = useToolUi(LINE_FILTER_STRINGS);
  const [text, setText] = useState("");
  const [terms, setTerms] = useState("");
  const [action, setAction] = useState<FilterAction>("keep");
  const [kind, setKind] = useState<MatchKind>("text");
  const [position, setPosition] = useState<MatchPosition>("contains");
  const [combine, setCombine] = useState<Combine>("any");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [trim, setTrim] = useState(DEFAULT_FILTER_OPTIONS.trim);
  const [keepBlank, setKeepBlank] = useState(false);
  const deferred = useDeferredValue(text);
  const deferredTerms = useDeferredValue(terms);

  const options: FilterOptions = useMemo(
    () => ({
      action,
      kind,
      position,
      combine,
      caseSensitive,
      wholeWord,
      trim,
      keepBlank,
    }),
    [action, kind, position, combine, caseSensitive, wholeWord, trim, keepBlank]
  );
  const result = useMemo(
    () =>
      deferred !== "" && deferredTerms.trim() !== ""
        ? filterLines(deferred, deferredTerms, options)
        : null,
    [deferred, deferredTerms, options]
  );

  async function openFile(file: File | undefined) {
    if (file) {
      setText(await file.text());
    }
  }

  const isText = kind === "text";
  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="lf-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sampleText);
            setTerms(strings.sampleTerms);
          }}
          type="button"
        >
          {strings.sample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0 && terms.length === 0}
          onClick={() => {
            setText("");
            setTerms("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={LABEL} htmlFor="lf-file">
          {strings.fileLabel}
        </label>
        <input
          accept=".txt,.csv,.tsv,.log,.md,.srt,.vtt,text/*"
          className="text-foreground text-sm"
          id="lf-file"
          onChange={(event) => {
            openFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <PillGroup
          label={strings.actionLabel}
          name="lf-action"
          onChange={setAction}
          options={(Object.keys(strings.actions) as FilterAction[]).map(
            (value) => ({ value, label: strings.actions[value] })
          )}
          value={action}
        />
        <PillGroup
          label={strings.kindLabel}
          name="lf-kind"
          onChange={setKind}
          options={(Object.keys(strings.kinds) as MatchKind[]).map((value) => ({
            value,
            label: strings.kinds[value],
          }))}
          value={kind}
        />
      </div>
      <TextAreaField
        hint={isText ? strings.termsHintText : strings.termsHintRegex}
        id="lf-terms"
        label={strings.termsLabel}
        onChange={setTerms}
        placeholder={strings.termsPlaceholder}
        rows={3}
        value={terms}
      />
      <div className="grid gap-5 sm:grid-cols-2">
        {isText ? (
          <SelectField
            id="lf-position"
            label={strings.positionLabel}
            onChange={setPosition}
            options={(Object.keys(strings.positions) as MatchPosition[]).map(
              (value) => ({ value, label: strings.positions[value] })
            )}
            value={position}
          />
        ) : null}
        <SelectField
          id="lf-combine"
          label={strings.combineLabel}
          onChange={setCombine}
          options={(Object.keys(strings.combines) as Combine[]).map(
            (value) => ({ value, label: strings.combines[value] })
          )}
          value={combine}
        />
        <div className="space-y-2">
          <Checkbox
            checked={caseSensitive}
            label={strings.caseSensitive}
            onChange={setCaseSensitive}
          />
          {isText ? (
            <Checkbox
              checked={wholeWord}
              label={strings.wholeWord}
              onChange={setWholeWord}
            />
          ) : null}
          <Checkbox checked={trim} label={strings.trim} onChange={setTrim} />
          <Checkbox
            checked={keepBlank}
            label={strings.keepBlank}
            onChange={setKeepBlank}
          />
        </div>
      </div>
      {result ? (
        <>
          <LineFilterErrors result={result} strings={strings} />
          {result.errors.length === 0 ? (
            <LineFilterResults result={result} strings={strings} />
          ) : null}
        </>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
